import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import agent from "../../../agent";
import PointDialog from "./PointDialog";

const storageText = (row) => {
    const { minStorageDays: min, maxStorageDays: max } = row;
    if (min == null && max == null) return "—";
    if (min != null && max != null) return min === max ? `${min} дн.` : `${min}–${max} дн.`;
    return min != null ? `от ${min} дн.` : `до ${max} дн.`;
};

/**
 * Реестр кросс-докинга графа: graph.cross_docking_point и график работы в
 * graph.cross_docking_point_time.
 *
 * Точка — место перегрузки в пункте, с адресом; перевозчика у неё нет. Граф
 * строит кросс-докинг через активную точку пункта (в пункте она одна), заказ
 * получает её название, адрес и график. Цена кросс-докинга — в «Тарифах
 * кросс-докинга», по точке.
 */
const CrossDockingPoints = () => {
    const toast = React.useRef(null);
    const [points, setPoints] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [query, setQuery] = React.useState("");
    // null — диалог закрыт, {} — новая точка, строка таблицы — правка
    const [editing, setEditing] = React.useState(null);

    const showMessage = (severity, summary, detail, life = 5000) =>
        toast.current?.show({ severity, summary, detail, life });

    const load = React.useCallback(() => {
        setLoading(true);
        agent.CrossDocking.getPoints()
            .then((res) => setPoints(Array.isArray(res) ? res : []))
            .catch((err) => showMessage("error", "Ошибка", agent.handleError(err)))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const visible = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        return points.filter((p) =>
            !q || `${p.locality} ${p.kato} ${p.name || ""} ${p.address || ""}`.toLowerCase().includes(q)
        );
    }, [points, query]);

    const onSaved = (saved) => {
        setEditing(null);
        showMessage("success", "Сохранено", `${saved.name || "Точка"}, ${saved.locality}`);
        load();
    };

    const localityBody = (row) => (
        <div>
            <div>{row.locality}</div>
            <small style={{ color: "#6b7280" }}>{row.kato}</small>
        </div>
    );

    const activeBody = (row) => (
        <Tag value={row.enabled ? "активна" : "выключена"} severity={row.enabled ? "success" : "secondary"} />
    );

    return (
        <div>
            <Toast ref={toast} />

            {editing && (
                <PointDialog
                    point={editing.id ? editing : null}
                    onSaved={onSaved}
                    onHide={() => setEditing(null)}
                />
            )}

            <div className="p-d-flex p-jc-between p-ai-center">
                <h1>Точки кросс-докинга</h1>
                <Button label="Добавить точку" icon="pi pi-plus" onClick={() => setEditing({})} />
            </div>

            <Message
                severity="info"
                style={{ marginBottom: 10 }}
                text="Граф строит путь с кросс-докингом через активную точку пункта, в пункте она одна. Заказ получает её название, адрес и график. Правка действует со следующего расчёта. Цены кросс-докинга — в «Тарифах кросс-докинга»."
            />

            <div className="p-d-flex p-ai-center p-flex-wrap" style={{ gap: 8, marginBottom: 10 }}>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Пункт, КАТО, название или адрес"
                               style={{ paddingLeft: "2.25rem", minWidth: 280 }} />
                </span>
                <small style={{ color: "#6b7280" }}>{visible.length} из {points.length}</small>
            </div>

            <DataTable value={visible} loading={loading} dataKey="id" paginator rows={25}
                       rowsPerPageOptions={[25, 50, 100]} sortField="locality" sortOrder={1}
                       emptyMessage="Точек нет">
                <Column header="Пункт" field="locality" body={localityBody} sortable />
                <Column header="Название" field="name" sortable />
                <Column header="Адрес" body={(row) => row.address || "—"} />
                <Column header="Срок хранения" body={storageText} />
                <Column header="График" field="time" />
                <Column header="Статус" field="enabled" body={activeBody} sortable />
                <Column body={(row) => (
                    <Button icon="pi pi-pencil" className="p-button-text p-button-rounded" onClick={() => setEditing(row)} />
                )} style={{ width: "4rem" }} />
            </DataTable>
        </div>
    );
};

export default CrossDockingPoints;
